// modules/admin/SiteBuilder/SiteBuilder.tsx - Site Builder com Drag & Drop

import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  MoveVertical,
  Globe,
  Smartphone,
  Monitor,
  Save,
  X
} from 'lucide-react';
import { TenantPage } from '@/core/types';
import { PageComponent } from '@/types';
import { ComponentList } from './components/ComponentList';
import { useAuth } from '../../auth/AuthContext';
import { getTenantCapabilities } from '../../../core/config/tenant-capabilities';

export const SiteBuilder: React.FC = () => {
  const { tenant } = useAuth();
  const capabilities = getTenantCapabilities(tenant?.businessType || 'lodge');

  const [pages, setPages] = useState<TenantPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<TenantPage | null>(null);
  const [showComponentPanel, setShowComponentPanel] = useState(false);
  const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isSaving, setIsSaving] = useState(false);
  
  // Componentes Base (definição estática)
  const allComponents = [
    { type: 'hero', name: 'Hero / Capa', icon: '🎯', description: 'Capa principal com título e imagem de fundo' },
    { type: 'about', name: 'Sobre Nós', icon: '🏨', description: 'Seção sobre a pousada/lodge' },
    { type: 'services', name: 'Serviços', icon: '🎣', description: 'Lista de serviços oferecidos' },
    { type: 'gallery', name: 'Galeria', icon: '🖼️', description: 'Galeria de fotos em grid' },
    { type: 'team', name: 'Equipe/Guias', icon: '👥', description: 'Apresentação da equipe' },
    { type: 'testimonials', name: 'Depoimentos', icon: '⭐', description: 'Avaliações de clientes' },
    { type: 'contact', name: 'Contato', icon: '📞', description: 'Formulário de contato' },
    { type: 'booking', name: 'Reservas', icon: '📅', description: 'Widget de reservas' },
    { type: 'pricing', name: 'Tarifas', icon: '💰', description: 'Tabela de preços' },
    { type: 'map', name: 'Localização', icon: '📍', description: 'Mapa Google Maps' },
    { type: 'rich_text', name: 'Texto Rico', icon: '📝', description: 'Bloco de texto formatado' },
    { type: 'image', name: 'Imagem', icon: '🖼️', description: 'Imagem simples' },
    // Novos componentes possivelmente não definidos explicitamente na lista original mas presentes em types
    { type: 'fishing-reports', name: 'Relatos de Pesca', icon: '🐟', description: 'Últimas pescarias' },
    { type: 'packages', name: 'Pacotes', icon: '📦', description: 'Pacotes promocionais' },
    { type: 'rooms', name: 'Acomodações', icon: '🛏️', description: 'Quartos e suítes' },
    { type: 'events', name: 'Eventos', icon: '📅', description: 'Próximos eventos' },
  ];

  // Filtrar componentes baseados no capability do tenant
  const availableComponents = allComponents.filter(c => 
    capabilities.allowedComponents.includes(c.type) || 
    ['header', 'footer'].includes(c.type) // Sempre permitir header/footer se estiverem na lista (não estão aqui, mas por segurança)
  );
  
  const handleAddPage = () => {
    const newPage: TenantPage = {
      id: `page-${Date.now()}`,
      title: 'Nova Página',
      slug: `nova-pagina-${Date.now()}`,
      type: 'custom',
      components: [],
      isPublished: false,
      isDraft: true,
      order: pages.length + 1,
      showInNavigation: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setPages([...pages, newPage]);
    setSelectedPage(newPage);
  };
  
  const handleAddComponent = (componentType: string) => {
    if (!selectedPage) return;
    
    // Using any cast to bypass strict check for now, as PageComponent data is a complex union
    const newComponent: any = {
      id: `comp-${Date.now()}`,
      type: componentType,
      order: selectedPage.components.length + 1,
      settings: { 
        isFullWidth: true,
        backgroundColor: '#ffffff',
        textColor: '#000000',
      },
      data: getDefaultComponentData(componentType)
    };
    
    const updatedPage = {
      ...selectedPage,
      components: [...selectedPage.components, newComponent],
      updatedAt: new Date()
    };
    
    setSelectedPage(updatedPage);
    setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedPage) return;
    
    const items = Array.from(selectedPage.components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Atualizar order
    const updatedComponents = items.map((comp, index) => ({
      ...comp,
      order: index + 1
    }));
    
    const updatedPage = {
      ...selectedPage,
      components: updatedComponents,
      updatedAt: new Date()
    };
    
    setSelectedPage(updatedPage);
    setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
  };

  const handleDeleteComponent = (componentId: string) => {
    if (!selectedPage) return;
    
    const updatedPage = {
      ...selectedPage,
      components: selectedPage.components.filter(c => c.id !== componentId),
      updatedAt: new Date()
    };
    
    setSelectedPage(updatedPage);
    setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
  };

  const handleSavePage = async () => {
    if (!selectedPage) return;
    
    setIsSaving(true);
    try {
      // Aqui você salvaria no Firebase
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simula salvamento
      alert('✅ Página salva com sucesso!');
    } catch (error) {
      alert('❌ Erro ao salvar página');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishPage = () => {
    if (!selectedPage) return;
    
    const updatedPage = {
      ...selectedPage,
      isPublished: true,
      isDraft: false,
      publishedAt: new Date(),
      updatedAt: new Date()
    };
    
    setSelectedPage(updatedPage);
    setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
    alert('✅ Página publicada!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-full px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Globe className="text-blue-600" size={28} />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Construtor de Páginas
                </h1>
                <p className="text-sm text-gray-600">
                  {selectedPage ? `Editando: ${selectedPage.title}` : 'Selecione uma página'}
                </p>
              </div>
            </div>
            
            {selectedPage && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 w-full md:w-auto">
                <button
                  onClick={() => setShowComponentPanel(!showComponentPanel)}
                  className="flex items-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 whitespace-nowrap text-sm font-bold shadow-lg"
                >
                  {showComponentPanel ? <X size={18} /> : <Plus size={18} />}
                  {showComponentPanel ? 'Fechar' : 'Adicionar'}
                </button>
                
                <button
                  onClick={handleSavePage}
                  disabled={isSaving}
                  className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 whitespace-nowrap text-sm font-bold"
                >
                  <Save size={18} />
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
                
                <button
                  onClick={handlePublishPage}
                  className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 whitespace-nowrap text-sm font-bold"
                >
                  <Eye size={18} />
                  Publicar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:grid md:grid-cols-12 md:gap-0 min-h-[calc(100vh-140px)] md:h-[calc(100vh-80px)]">
        {/* Painel Esquerdo: Lista de Páginas */}
        <div className="w-full md:col-span-2 bg-white border-r border-b md:border-b-0 overflow-y-auto max-h-48 md:max-h-full">
          <div className="p-4">
            <button
              onClick={handleAddPage}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 mb-4"
            >
              <Plus size={18} />
              Nova Página
            </button>
            
            <div className="space-y-2">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedPage?.id === page.id
                      ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                  onClick={() => setSelectedPage(page)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm truncate">{page.title}</span>
                    <button 
                        className="p-1 hover:bg-red-50 text-red-600 rounded"
                        title="Excluir Página"
                        aria-label="Excluir Página"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">/{page.slug}</div>
                  <div className="flex items-center gap-2 mt-2">
                    {page.isPublished ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        Publicada
                      </span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                        Rascunho
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {page.components.length} comp.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Painel Central: Pré-visualização */}
        <div className={`flex-1 ${showComponentPanel ? 'md:col-span-7' : 'md:col-span-10'} bg-gray-100 overflow-y-auto p-4 md:p-6 min-h-[60vh] md:min-h-0`}>
          {selectedPage ? (
            <div>
              {/* Controles de visualização */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex bg-white rounded-lg p-1 shadow-sm">
                  <button
                    onClick={() => setViewMode('desktop')}
                    className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                    title="Desktop"
                  >
                    <Monitor size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('tablet')}
                    className={`p-2 rounded ${viewMode === 'tablet' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                    title="Tablet"
                  >
                    <Smartphone size={20} className="rotate-90" />
                  </button>
                  <button
                    onClick={() => setViewMode('mobile')}
                    className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                    title="Mobile"
                  >
                    <Smartphone size={20} />
                  </button>
                </div>
              </div>
              
              {/* Preview Area */}
              <div className={`bg-white rounded-lg shadow-xl mx-auto transition-all ${
                viewMode === 'mobile' ? 'max-w-sm' : viewMode === 'tablet' ? 'max-w-2xl' : 'max-w-full'
              }`}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="page-preview">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="min-h-[600px] p-4"
                      >
                        {selectedPage.components.length === 0 ? (
                          <div className="text-center py-20 text-gray-400">
                            <Globe size={64} className="mx-auto mb-4 opacity-20" />
                            <p className="text-lg">Página vazia</p>
                            <p className="text-sm">Adicione componentes usando o botão "Adicionar" acima</p>
                          </div>
                        ) : (
                          selectedPage.components
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                            .map((component, index) => (
                              <Draggable
                                key={component.id}
                                draggableId={component.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`group relative border-2 rounded-lg mb-4 transition-all ${
                                      snapshot.isDragging
                                        ? 'border-blue-500 shadow-2xl bg-blue-50'
                                        : 'border-transparent hover:border-blue-300'
                                    }`}
                                  >
                                    {/* Header do Componente */}
                                    <div className="absolute top-0 left-0 right-0 bg-gray-900 bg-opacity-90 text-white p-2 rounded-t-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-between z-10">
                                      <div className="flex items-center gap-2">
                                        <span {...provided.dragHandleProps} className="cursor-move">
                                          <MoveVertical size={16} />
                                        </span>
                                        <span className="text-sm font-medium">
                                          {availableComponents.find(c => c.type === component.type)?.icon}
                                          {' '}
                                          {availableComponents.find(c => c.type === component.type)?.name}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button 
                                            className="p-1 hover:bg-gray-700 rounded"
                                            title="Editar Componente"
                                            aria-label="Editar Componente"
                                        >
                                          <Edit size={16} />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteComponent(component.id)}
                                          className="p-1 hover:bg-red-600 rounded"
                                          title="Remover Componente"
                                          aria-label="Remover Componente"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Preview do Componente */}
                                    <div className="p-6 bg-gray-50 rounded-lg">
                                      <ComponentPreview component={component} />
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500">
              <Globe size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-xl">Nenhuma página selecionada</p>
              <button
                onClick={handleAddPage}
                className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
              >
                Criar sua primeira página →
              </button>
            </div>
          )}
        </div>
        
        {/* Painel Direito: Componentes */}
        {showComponentPanel && (
          <div className="fixed inset-0 z-[60] md:static md:z-auto bg-black/50 md:bg-transparent flex flex-col justify-end md:block md:col-span-3">
             {/* Mobile Close Layer */}
             <div className="flex-1 md:hidden" onClick={() => setShowComponentPanel(false)} />
             
             {/* Panel Content */}
             <div className="w-full bg-white border-t md:border-l md:border-t-0 rounded-t-2xl md:rounded-none h-[70vh] md:h-full overflow-y-auto shadow-2xl md:shadow-none animate-slide-up md:animate-none">
                <div className="p-4 border-b md:hidden flex justify-between items-center sticky top-0 bg-white z-10">
                   <h3 className="font-bold text-gray-800">Adicionar Componente</h3>
                   <button onClick={() => setShowComponentPanel(false)} className="p-2 bg-gray-100 rounded-full" title="Fechar Painel" aria-label="Fechar Painel"><X size={20}/></button>
                </div>
                <ComponentList
                  components={availableComponents}
                  onAddComponent={(type) => {
                      handleAddComponent(type);
                      // Close on mobile after adding
                      if (window.innerWidth < 768) setShowComponentPanel(false);
                  }}
                />
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper para dados padrão de cada tipo de componente
function getDefaultComponentData(type: string): Record<string, any> {
  const defaults: Record<string, any> = {
    hero: {
      title: 'Bem-vindo',
      subtitle: 'Sua mensagem aqui',
       backgroundImage: '',
      buttons: []
    },
    about: {
      title: 'Sobre Nós',
      content: 'Conte sua história...',
      image: ''
    },
    services: {
      title: 'Nossos Serviços',
      services: []
    },
    gallery: {
      title: 'Galeria',
      images: []
    },
    testimonials: {
      title: 'Depoimentos',
      testimonials: []
    },
    contact: {
      title: 'Entre em Contato',
      fields: ['name', 'email', 'phone', 'message']
    },
    pricing: {
      title: 'Tarifas',
      plans: []
    },
    team: {
      title: 'Nossa Equipe',
      members: []
    },
    rich_text: {
      content: '<p>Digite seu texto aqui...</p>',
      alignment: 'left'
    },
    image: {
      src: '',
      alt: '',
      caption: ''
    }
  };
  
  return defaults[type] || {};
}

// Componente de Preview Simples
const ComponentPreview: React.FC<{ component: PageComponent }> = ({ component }) => {
  return (
    <div className="text-center p-4 md:p-8 border-2 border-dashed border-gray-300 rounded">
      <div className="text-4xl mb-2">
        {component.type === 'hero' && '🎯'}
        {component.type === 'about' && '🏨'}
        {component.type === 'services' && '🎣'}
        {component.type === 'gallery' && '🖼️'}
        {component.type === 'team' && '👥'}
        {component.type === 'testimonials' && '⭐'}
        {component.type === 'contact' && '📞'}
        {component.type === 'booking' && '📅'}
        {component.type === 'pricing' && '💰'}
        {component.type === 'map' && '📍'}
        {component.type === 'rich_text' && '📝'}
        {component.type === 'image' && '🖼️'}
      </div>
      <h3 className="font-bold text-lg capitalize">{component.type.replace('_', ' ')}</h3>
      <p className="text-sm text-gray-500 mt-1">
        {(component.data as any)?.title || (component.data as any)?.content?.substring(0, 50) || 'Configure este componente'}
      </p>
    </div>
  );
};
